import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

export interface DotLoaderProps {
  /** Dot diameter. sm = 4px (inline in text / buttons), md = 6px, lg = 8px. */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Announce the pending state. Default `true` → `role="status"` + an
   * accessible name. Set `false` when the loader sits inside a control that
   * already announces its own busy state (a `Button` with `aria-busy`) —
   * announcing both double-speaks (§55).
   */
  announce?: boolean
  /** Accessible name when `announce` is true. */
  label?: string
  className?: string
}

const DOT_SIZE: Record<NonNullable<DotLoaderProps['size']>, string> = {
  sm: 'h-1 w-1',
  md: 'h-1.5 w-1.5',
  lg: 'h-2 w-2',
}

const GAP: Record<NonNullable<DotLoaderProps['size']>, string> = {
  sm: 'gap-1',
  md: 'gap-1.5',
  lg: 'gap-2',
}

/**
 * The project's one loader (§6, §55). Three `bg-current` dots that pulse and
 * bob — inherits colour from the parent, so it works on a brand CTA, on a card
 * and on a dark surface with no per-site tokens.
 *
 * Replaces five inline copies (LoginPage, RegisterPage, ForgotPasswordPage,
 * ResetPasswordPage, DashboardPage) and is the preferred alternative to
 * `CircleNotch` + `animate-spin`.
 *
 * Reduced motion: renders the three dots static at a legible opacity rather
 * than animating — the shape still reads as "pending", nothing moves.
 */
export const DotLoader: React.FC<DotLoaderProps> = ({
  size = 'sm',
  announce = true,
  label = 'Loading',
  className,
}) => {
  const reduced = useReducedMotion()

  const a11y = announce
    ? ({ role: 'status', 'aria-label': label } as const)
    : ({ 'aria-hidden': true } as const)

  return (
    <span className={cn('inline-flex items-center', GAP[size], className)} {...a11y}>
      {[0, 1, 2].map((i) =>
        reduced ? (
          <span
            key={i}
            className={cn('block rounded-full bg-current', DOT_SIZE[size])}
            style={{ opacity: 0.4 + i * 0.2 }}
          />
        ) : (
          <motion.span
            key={i}
            className={cn('block rounded-full bg-current', DOT_SIZE[size])}
            animate={{ opacity: [0.25, 1, 0.25], y: [0, -2, 0] }}
            transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }}
          />
        ),
      )}
    </span>
  )
}

export default DotLoader
